import { useMemo } from 'react';
import { useQuery, gql } from '@apollo/client';

const GET_SEARCH_SIZE_FILTER_FOR_VARS = gql`
  query GetSearchSizeFilter {
    getSearchSizeFilter {
      size_type_id
      size_type_name
      size_values {
        id
        name
        sort_order
      }
    }
  }
`;

/**
 * 검색 쿼리에 사용될 변수를 생성하는 커스텀 훅.
 * 키워드, 필터, 정렬 값의 변경을 감지하여 변수를 메모이제이션합니다.
 * @param {string} keyword - 검색 키워드
 * @param {object} filters - 적용할 필터 객체
 * @param {string} sortValue - 정렬 순서
 * @returns {object} GraphQL 쿼리 변수
 */
const useProductSearchVariables = (keyword, filters, sortValue) => {
    const hasSizeFilter = (filters?.sizeValueIds?.length ?? 0) > 0;
    const { data: sizeFilterData } = useQuery(GET_SEARCH_SIZE_FILTER_FOR_VARS, {
        fetchPolicy: 'cache-first',
        skip: !hasSizeFilter,
    });

    const sizeValueIdToTypeId = useMemo(() => {
        const map = new Map();
        const list = sizeFilterData?.getSearchSizeFilter || [];
        list.forEach((group) => {
            (group.size_values || []).forEach((v) => {
                map.set(Number(v.id), Number(group.size_type_id));
            });
        });
        return map;
    }, [sizeFilterData]);

    return useMemo(() => {
        const filterVariables = { query: keyword };

        if (filters) {
            const {
                gender,
                subcategoryIds,
                priceMin,
                priceMax,
                sizeValueIds,
                brands,
                isSelling,
                condition,
            } = filters;

            if (gender) {
                filterVariables.gender = gender;
            }
            if (subcategoryIds && subcategoryIds.length > 0) {
                filterVariables.subcategory_ids = subcategoryIds;
            }
            if (priceMin !== null && priceMin !== undefined) {
                filterVariables.price_min = priceMin;
            }
            if (priceMax !== null && priceMax !== undefined) {
                filterVariables.price_max = priceMax;
            }
            if (sizeValueIds && sizeValueIds.length > 0) {
                const groupedByType = new Map();
                sizeValueIds.forEach((id) => {
                    const valueId = Number(id);
                    const typeId = sizeValueIdToTypeId.get(valueId);
                    if (!typeId) return;
                    if (!groupedByType.has(typeId)) {
                        groupedByType.set(typeId, []);
                    }
                    groupedByType.get(typeId).push(valueId);
                });
                if (groupedByType.size > 0) {
                    filterVariables.size_filter = Array.from(
                        groupedByType.entries()
                    ).map(([size_type_id, size_value_ids]) => ({
                        size_type_id,
                        size_value_ids,
                    }));
                }
            }
            if (brands && brands.length > 0) {
                filterVariables.brand = brands.map(b => (typeof b === 'string' ? b : b.name));
            }
            if (isSelling) {
                filterVariables.show_only = 'selling';
            }
            if (condition) {
                filterVariables.condition = 'NEW';
            }
        }

        return {
            filter: filterVariables,
            sort: sortValue,
        };
    }, [keyword, filters, sortValue, sizeValueIdToTypeId]);
};

export default useProductSearchVariables;
